import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Modal } from '@/components/ui';
import type { KeycloakAccountRow } from '../types';
import styles from './accountDialogs.module.css';

interface Props {
    row: KeycloakAccountRow | null;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onConfirm: (endSessions: boolean) => void;
}

/**
 * EAA-1820 (frame 80): lock confirm launched from the profile drawer — simpler
 * than the row-kebab disable (frame 86) because the operator is already looking
 * at the account. No username re-type; the amber "Khoá khác xoá" box carries
 * the distinction from delete.
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the end-sessions checkbox swapped
 * to the kit `Checkbox`.
 */
export function LockAccountDialog({ row, pending, error, onClose, onConfirm }: Props) {
    const { t } = useTranslation();
    const [endSessions, setEndSessions] = useState(false);
    const open = Boolean(row);

    return (
        // EAA-1804: root debugging handle, matching the folder's existing acc-* prefix.
        <Modal
            className="acc-lock-dialog"
            open={open}
            onCancel={onClose}
            title={t('console.accounts.lockModal.titleForUser', { username: row?.username })}
            footer={
                <>
                    <span className={styles.deleteFooterNote}>{t('console.accounts.lockModal.auditFooter')}</span>
                    <Button onClick={onClose} disabled={pending}>
                        {t('common.cancel')}
                    </Button>
                    <Button danger onClick={() => onConfirm(endSessions)} disabled={pending}>
                        {pending ? t('console.accounts.lockModal.pending') : t('console.accounts.lockModal.submit')}
                    </Button>
                </>
            }
        >
            <p className="portal-admin-hint">{t('console.accounts.lockModal.hint', { username: row?.username })}</p>

            <div className={styles.lockWarnBox} role="note">
                <strong>{t('console.accounts.lockModal.diffHeading')}</strong>
                <p className={styles.lockWarnBody}>{t('console.accounts.lockModal.diffBody')}</p>
            </div>

            <Checkbox
                className={styles.lockSessionsOpt}
                checked={endSessions}
                onChange={(event) => setEndSessions(event.target.checked)}
            >
                <span>
                    {t('console.accounts.lockModal.sessionsLabel')}
                    <span className={styles.lockSessionsSub}>{t('console.accounts.lockModal.sessionsSub')}</span>
                </span>
            </Checkbox>

            {error && <p className="portal-admin-error">{error}</p>}
        </Modal>
    );
}

export default LockAccountDialog;
